const EMAIL_WRITER_POLLING_SESSION_PREFIX = "email-writer-polling:";

const emailWriterCompletionToastShown = new Set<string>();

type EmailWriterPollingSession = {
  clientId: string;
  touchpointId?: string;
  touchpointIds?: string[];
};

export type MarkEmailWriterPollingStartedOptions = {
  /** When set, completion polling targets this touchpoint detail only (rewrite). */
  touchpointId?: string;
  touchpointIds?: string[];
};

export const getEmailWriterPollingSessionKey = (campaignId: string) =>
  `${EMAIL_WRITER_POLLING_SESSION_PREFIX}${campaignId}`;

export const hasEmailWriterCompletionToastBeenShown = (campaignId: string) =>
  emailWriterCompletionToastShown.has(campaignId);

export const markEmailWriterCompletionToastShown = (campaignId: string) => {
  emailWriterCompletionToastShown.add(campaignId);
};

export const resetEmailWriterCompletionToastTracking = (campaignId: string) => {
  emailWriterCompletionToastShown.delete(campaignId);
};

const parseEmailWriterPollingSession = (raw: string | null): EmailWriterPollingSession | null => {
  if (raw === null) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;

    if (
      parsed &&
      typeof parsed === "object" &&
      "clientId" in parsed &&
      typeof (parsed as EmailWriterPollingSession).clientId === "string"
    ) {
      const session = parsed as EmailWriterPollingSession;

      return {
        clientId: session.clientId,
        touchpointId: typeof session.touchpointId === "string" ? session.touchpointId : undefined,
        touchpointIds: Array.isArray(session.touchpointIds)
          ? session.touchpointIds.filter((id): id is string => typeof id === "string")
          : undefined,
      };
    }
  } catch {
    // Legacy sessions stored a bare clientId string.
  }

  return { clientId: raw };
};

const readEmailWriterPollingSession = (campaignId: string): EmailWriterPollingSession | null =>
  parseEmailWriterPollingSession(sessionStorage.getItem(getEmailWriterPollingSessionKey(campaignId)));

export const markEmailWriterPollingStarted = (
  campaignId: string,
  clientId: string,
  options?: MarkEmailWriterPollingStartedOptions,
) => {
  resetEmailWriterCompletionToastTracking(campaignId);

  const session: EmailWriterPollingSession = {
    clientId,
    ...(options?.touchpointId ? { touchpointId: options.touchpointId } : {}),
    ...(options?.touchpointIds?.length ? { touchpointIds: options.touchpointIds } : {}),
  };

  sessionStorage.setItem(getEmailWriterPollingSessionKey(campaignId), JSON.stringify(session));
  window.dispatchEvent(new Event("email-writer-polling-session-changed"));
};

export const clearEmailWriterPollingSession = (campaignId: string) => {
  sessionStorage.removeItem(getEmailWriterPollingSessionKey(campaignId));
  window.dispatchEvent(new Event("email-writer-polling-session-changed"));
};

export const hasEmailWriterPollingSession = (campaignId: string, clientId?: string): boolean => {
  const session = readEmailWriterPollingSession(campaignId);

  if (!session) {
    return false;
  }

  if (!clientId) {
    return true;
  }

  return session.clientId === clientId || session.clientId === "true";
};

/** Touchpoint scoped for single-email rewrite; undefined for campaign-wide create-with-writer. */
export const getEmailWriterPollingFocusTouchpointId = (campaignId: string): string | undefined =>
  readEmailWriterPollingSession(campaignId)?.touchpointId;

export const getEmailWriterPollingScopedTouchpointIds = (campaignId: string): string[] | undefined =>
  readEmailWriterPollingSession(campaignId)?.touchpointIds;

export const getActiveEmailWriterPollingCampaignIds = (clientId: string): string[] => {
  const campaignIds: string[] = [];

  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);

    if (!key?.startsWith(EMAIL_WRITER_POLLING_SESSION_PREFIX)) {
      continue;
    }

    const campaignId = key.slice(EMAIL_WRITER_POLLING_SESSION_PREFIX.length);

    if (hasEmailWriterPollingSession(campaignId, clientId)) {
      campaignIds.push(campaignId);
    }
  }

  return campaignIds;
};
